import type { Ref } from 'react';
import { AlertTriangle, Loader2, MonitorPlay } from 'lucide-react';
import { formatMoney } from '@/shared/lib/format/money';
import { Row as BillRow } from '@/features/campaigns/components/PreviewStep/PreviewStepPrimitives';

export type BillSummaryProps = {
  total: number;
  subtotal: number;
  taxRows: Array<{ label: string; value: number }>;
  discount: number;
  budget: number | null;
  overBudget: boolean;
  availableBalance: number;
  balanceOk: boolean;
  /** Pay & Launch only shows on the last step. */
  showLaunch: boolean;
  launchDisabled: boolean;
  isLaunching: boolean;
  needsTopUp: boolean;
  blockedReason: string | null;
  /** The price is being refreshed on the server. */
  updating?: boolean;
  /** The total is an estimate until the plan is confirmed on Pay. */
  estimated?: boolean;
  /** Pay was pressed and the plan is being confirmed. */
  saving?: boolean;
  /** How far the total is over the budget (0 when within it) and whether the user agreed to pay it. */
  overBudgetAmount?: number;
  overBudgetAccepted?: boolean;
  onOverBudgetAcceptedChange?: (accepted: boolean) => void;
  /** The wallet can pay the whole total; otherwise this much more must be added. */
  walletCoversPayable?: boolean;
  walletShortfall?: number;
  onLaunch: () => void;
  /** Shown above the Pay button, for example when a creative for one orientation is missing. */
  warning?: string | null;
  /** What the Pay button says; changes to "Pay anyway" once a warning has been shown. */
  payLabel?: string;
};

/** Subtotal, GST, discount and wallet rows. */
export function BillRows({
  subtotal,
  taxRows,
  discount,
  availableBalance,
  balanceOk,
}: Pick<BillSummaryProps, 'subtotal' | 'taxRows' | 'discount' | 'availableBalance' | 'balanceOk'>) {
  return (
    <div className="space-y-2 rounded-xl bg-[var(--dash-soft)] p-3 text-sm">
      <BillRow label="Subtotal" value={formatMoney(subtotal)} />
      {taxRows.map((row, index) => (
        <BillRow key={`${row.label}-${index}`} label={row.label} value={formatMoney(row.value)} />
      ))}
      {discount > 0 ? <BillRow label="Discount" value={`−${formatMoney(discount)}`} tone="success" /> : null}
      <BillRow label="Wallet" value={formatMoney(availableBalance)} tone={balanceOk ? 'success' : 'warn'} />
    </div>
  );
}

/** The box a user ticks to pay the amount that goes past their budget. */
export function OverBudgetConfirm({
  overBudgetAmount = 0,
  overBudgetAccepted = false,
  onOverBudgetAcceptedChange,
  walletCoversPayable = true,
  walletShortfall = 0,
  availableBalance,
}: Pick<
  BillSummaryProps,
  | 'overBudgetAmount'
  | 'overBudgetAccepted'
  | 'onOverBudgetAcceptedChange'
  | 'walletCoversPayable'
  | 'walletShortfall'
  | 'availableBalance'
>) {
  if (overBudgetAmount <= 0.005) return null;
  return (
    <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-xs leading-relaxed text-red-950">
      <input
        type="checkbox"
        checked={overBudgetAccepted}
        onChange={(event) => onOverBudgetAcceptedChange?.(event.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--dash-brand)]"
      />
      <span>
        <span className="block font-semibold">This is {formatMoney(overBudgetAmount)} over your budget.</span>
        {walletCoversPayable
          ? `Pay the extra ${formatMoney(overBudgetAmount)} from my wallet balance (${formatMoney(availableBalance)}).`
          : `Go over budget. My wallet is ${formatMoney(walletShortfall)} short, so I'll add that amount to pay.`}
      </span>
    </label>
  );
}

/** A caution shown above the Pay button. The first press on Pay shows it; the next press goes ahead. */
export function PayWarning({ warning }: { warning?: string | null }) {
  if (!warning) return null;
  return (
    <div className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-950" role="alert">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
      <span>{warning}</span>
    </div>
  );
}

/** The Pay & Launch button with its busy states. */
export function PayButton({
  launchDisabled,
  isLaunching,
  saving = false,
  needsTopUp,
  onLaunch,
  payLabel = 'Pay & Launch',
  className = '',
}: Pick<BillSummaryProps, 'launchDisabled' | 'isLaunching' | 'saving' | 'needsTopUp' | 'onLaunch' | 'payLabel'> & {
  className?: string;
}) {
  return (
    <button
      type="button"
      disabled={launchDisabled}
      onClick={onLaunch}
      className={`dash-btn-gradient inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    >
      {isLaunching || saving ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          {saving ? 'Confirming your plan…' : needsTopUp ? 'Processing payment…' : 'Launching…'}
        </>
      ) : (
        <>{payLabel}</>
      )}
    </button>
  );
}

/** The full bill: sticky beside the plan on desktop, and inline at the end of the page on phones. */
export function BillSummary({ innerRef, ...props }: BillSummaryProps & { innerRef?: Ref<HTMLElement> }) {
  const {
    total,
    budget,
    overBudget,
    showLaunch,
    blockedReason,
    updating = false,
    estimated = false,
    saving = false,
    launchDisabled,
    isLaunching,
    needsTopUp,
    onLaunch,
    warning,
    payLabel,
  } = props;
  const remaining = budget != null ? budget - total : null;

  return (
    <aside ref={innerRef} className="camp-section h-fit text-left" aria-label="Bill summary">
      <div className="mb-1 flex items-center gap-2">
        <MonitorPlay className="h-4 w-4 text-[var(--dash-brand)]" />
        <h2 className="m-0 text-[15px] font-semibold text-[var(--dash-ink)]">Bill summary</h2>
      </div>

      <div className="mt-3">
        <p className="m-0 text-xs text-[var(--dash-muted)]">Total (incl. GST)</p>
        <p
          className={`m-0 text-2xl font-semibold transition-opacity ${overBudget ? 'text-red-600' : 'text-[var(--dash-ink)]'} ${updating ? 'opacity-60' : ''}`}
          aria-live="polite"
        >
          {formatMoney(total)}
        </p>
        {estimated ? (
          <p className="m-0 mt-1 text-xs text-[var(--dash-muted)]">Estimate. The final price is confirmed when you pay.</p>
        ) : null}
        {budget != null ? (
          <p className={`m-0 mt-1 text-xs ${overBudget ? 'text-red-600' : 'text-[var(--dash-muted)]'}`}>
            {overBudget
              ? `${formatMoney(total - budget)} over your ${formatMoney(budget)} budget`
              : `of your ${formatMoney(budget)} budget · ${formatMoney(remaining ?? 0)} left`}
          </p>
        ) : null}
      </div>

      <div className="mt-4">
        <BillRows {...props} />
      </div>

      <div className="mt-4 empty:hidden">
        <OverBudgetConfirm {...props} />
      </div>

      {showLaunch ? (
        <>
          <div className="mt-4 empty:hidden">
            <PayWarning warning={warning} />
          </div>
          <PayButton
            launchDisabled={launchDisabled}
            isLaunching={isLaunching}
            saving={saving}
            needsTopUp={needsTopUp}
            onLaunch={onLaunch}
            payLabel={payLabel}
            className="mt-4 w-full"
          />
          {blockedReason ? (
            <p className="mt-2 mb-0 text-sm text-[var(--dash-muted)]">{blockedReason}</p>
          ) : (
            <p className="mt-2 mb-0 text-sm text-[var(--dash-success)]">Ready to launch</p>
          )}
        </>
      ) : null}
    </aside>
  );
}
